import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { api } from 'services/api';
import {
  Container, Content, StepIndicator, StepDot, Card, CardHeader, CardBody,
  MetaButton, QrCodeContainer, QrCodeImage, QrCodeInstructions, SuccessIcon,
  ConnectedCard, DisconnectButton, Spinner, ErrorMessage, BackButton,
  StepTitle, StepSubtitle, MetaLogo, QrCodeStep, QrCodeStepNumber, QrCodeStepText,
} from './styles';
import { Step, ConnectionStatus } from './types';

const META_APP_ID = process.env.REACT_APP_META_APP_ID || '';
const META_CONFIG_ID = process.env.REACT_APP_META_CONFIG_ID || '';
const STEPS: Step[] = ['intro', 'oauth', 'qrcode', 'success'];

// Dimensoes do popup — igual ao padrao da Meta
const POPUP_WIDTH = 600;
const POPUP_HEIGHT = 700;

export function MetaConnect() {
  const history = useHistory();

  const [step, setStep] = useState<Step>('intro');
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [deepLink, setDeepLink] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus | null>(null);

  const popupRef = useRef<Window | null>(null);
  const pollRef = useRef<number | null>(null);

  useEffect(() => {
    checkCurrentStatus();

    const handleMessage = (event: MessageEvent) => {
      const allowedOrigins = [
        'https://www.facebook.com',
        'https://web.facebook.com',
        'https://business.facebook.com',
      ];
      if (!allowedOrigins.includes(event.origin)) return;

      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        console.log('[MetaConnect] postMessage recebido de', event.origin, ':', data);

        if (data.type === 'WA_EMBEDDED_SIGNUP') {
          if (data.event === 'FINISH') {
            console.log('[MetaConnect] FINISH:', data.data);
            closePopup();
            handleConnectWithCode(data.data?.code ?? null, data.data?.phone_number_id ?? null, data.data?.waba_id ?? null);
          } else if (data.event === 'CANCEL') {
            console.warn('[MetaConnect] CANCEL:', data.data?.current_step);
            closePopup();
            setIsLoading(false);
          } else if (data.event === 'ERROR') {
            console.error('[MetaConnect] ERROR:', data.data?.error_message);
            closePopup();
            setError('Erro no cadastro Meta: ' + data.data.error_message);
            setIsLoading(false);
          }
          return;
        }

        if (data.type === 'HOSTED_ES_DONE' && data.code) {
          console.log('[MetaConnect] HOSTED_ES_DONE — code:', data.code);
          closePopup();
          handleConnectWithCode(data.code, null, null);
          return;
        }

        if (data.code && typeof data.code === 'string') {
          console.log('[MetaConnect] code recebido via postMessage:', data.code);
          closePopup();
          handleConnectWithCode(data.code, data.phone_number_id ?? null, data.waba_id ?? null);
        }
      } catch {
        // mensagens nao-JSON — ignorar
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
      closePopup();
    };
  }, []);

  function closePopup() {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    popupRef.current = null;
  }

  async function checkCurrentStatus() {
    setIsCheckingStatus(true);
    try {
      const { data } = await api.get('/meta/coexistence/status');
      if (data.data?.connected) {
        setConnectionStatus(data.data);
        setStep('connected' as Step);
      } else {
        setStep('intro');
      }
    } catch {
      setStep('intro');
    } finally {
      setIsCheckingStatus(false);
    }
  }

  async function handleConnectWithCode(
    code: string | null,
    phoneNumberId: string | null,
    wabaId: string | null,
  ) {
    if (!code) {
      setError('Nao foi possivel obter o codigo de autorizacao. Tente novamente.');
      setIsLoading(false);
      return;
    }

    try {
      const payload = { code, phone_number_id: phoneNumberId, waba_id: wabaId };
      console.log('[MetaConnect] POST /meta/connect:', payload);

      const { data } = await api.post('/meta/connect', payload);
      console.log('[MetaConnect] resposta /meta/connect:', data);

      const qr = data.data?.qr_data;
      if (qr?.qr_code_url) setQrCodeUrl(qr.qr_code_url);
      if (qr?.deep_link_url) setDeepLink(qr.deep_link_url);
      setStep('qrcode');
    } catch (err: any) {
      const serverMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.response?.data?.details ||
        null;
      console.error('[MetaConnect] erro /meta/connect:', err?.response?.data ?? err);
      setError(serverMsg ?? 'Erro ao conectar o numero. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  }

  function launchWhatsAppSignup() {
    setError(null);
    setIsLoading(true);

    const extras = JSON.stringify({
      sessionInfoVersion: '3',
      version: 'v3',
      featureType: 'whatsapp_business_app_onboarding',
      partner_data: 'null',
      is_hosted_es: true,
      response_type: 'code',
      override_default_response_type: true,

    });

    const callbackExtras = JSON.stringify({ sessionInfoVersion: '3', version: 'v4' });
    const callbackBase = 'https://business.facebook.com/messaging/hosted_es/oauth_callback/';
    const callbackUrl = `${callbackBase}?app_id=${META_APP_ID}&config_id=${META_CONFIG_ID}&extras=${encodeURIComponent(callbackExtras)}`;

    const params = new URLSearchParams({
      client_id: META_APP_ID,
      config_id: META_CONFIG_ID,
      display: 'popup',
      extras: extras,
      fallback_redirect_uri: callbackUrl,
      override_default_response_type: 'true',
      redirect_uri: callbackUrl,
      response_type: 'code',
    });

    const url = `https://www.facebook.com/v25.0/dialog/oauth?${params.toString()}`;
    console.log('[MetaConnect] abrindo popup:', url);

    const left = Math.round(window.screenX + (window.outerWidth - POPUP_WIDTH) / 2);
    const top = Math.round(window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2);

    const popup = window.open(
      url,
      'MetaEmbeddedSignup',
      `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes`,
    );

    if (!popup) {
      setError('Popup bloqueado pelo navegador. Permita popups para este site e tente novamente.');
      setIsLoading(false);
      return;
    }

    popupRef.current = popup;

    pollRef.current = window.setInterval(() => {
      if (popup.closed) {
        console.log('[MetaConnect] popup fechado pelo usuario');
        clearInterval(pollRef.current!);
        pollRef.current = null;
        setTimeout(() => {
          setIsLoading((prev) => {
            if (prev) {
              setError(null);
              return false;
            }
            return prev;
          });
        }, 500);
      }
    }, 500);
  }

  async function handleDisconnect() {
    setIsLoading(true);
    try {
      await api.delete('/meta/disconnect');
      setConnectionStatus(null);
      setStep('intro');
      setQrCodeUrl(null);
      setDeepLink(null);
      setError(null);
    } catch {
      setError('Erro ao desconectar. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  }

  function handleQrCodeDone() { setStep('success'); checkCurrentStatus(); }
  function getStepIndex(s: Step) { return STEPS.indexOf(s); }

  if (isCheckingStatus) return <Container><Content><Spinner /></Content></Container>;

  return (
    <Container>
      <Content>
        <BackButton onClick={() => history.push('/')}>← Voltar</BackButton>

        {step !== 'connected' && step !== 'intro' && (
          <StepIndicator>
            {(['oauth', 'qrcode', 'success'] as Step[]).map((s) => (
              <StepDot key={s} active={getStepIndex(step) >= getStepIndex(s)} />
            ))}
          </StepIndicator>
        )}

        {step === 'intro' && (
          <Card>
            <CardHeader>
              <MetaLogo>
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z" fill="currentColor" />
                </svg>
              </MetaLogo>
              <StepTitle>Conectar WhatsApp Business</StepTitle>
              <StepSubtitle>
                Conecte sua conta Meta para usar o WhatsApp Business com{' '}
                <strong>coexistencia</strong> — continue usando o app no celular e
                receba mensagens aqui simultaneamente.
              </StepSubtitle>
            </CardHeader>
            <CardBody>
              <ul>
                <li>Autorize o acesso a sua conta Meta Business</li>
                <li>Selecione seu numero do WhatsApp Business existente</li>
                <li>Escaneie o QR code para ativar a coexistencia</li>
                <li>Continue usando o WhatsApp normalmente no celular</li>
              </ul>
              <MetaButton onClick={() => { setStep('oauth'); setError(null); }}>
                <FbIcon /> Continuar com Meta
              </MetaButton>
            </CardBody>
          </Card>
        )}

        {step === 'oauth' && (
          <Card>
            <CardHeader>
              <StepTitle>Autorizar acesso</StepTitle>
              <StepSubtitle>
                Clique no botao abaixo. Na tela da Meta, em{' '}
                <strong>"Conta do WhatsApp Business"</strong>, selecione{' '}
                <strong>"Conectar um app do WhatsApp Business"</strong> para ativar
                a coexistencia com seu numero atual.
              </StepSubtitle>
            </CardHeader>
            <CardBody>
              {error && <ErrorMessage>{error}</ErrorMessage>}
              {isLoading ? <Spinner /> : (
                <MetaButton onClick={launchWhatsAppSignup}>
                  <FbIcon /> Iniciar cadastro com Meta
                </MetaButton>
              )}
            </CardBody>
          </Card>
        )}

        {step === 'qrcode' && (
          <Card>
            <CardHeader>
              <StepTitle>Ativar coexistencia</StepTitle>
              <StepSubtitle>
                Escaneie o QR code com o WhatsApp no celular. Voce continuara usando o app normalmente.
              </StepSubtitle>
            </CardHeader>
            <CardBody>
              {error && <ErrorMessage>{error}</ErrorMessage>}
              {isLoading ? <Spinner /> : (
                <>
                  <QrCodeContainer>
                    {qrCodeUrl ? (
                      <QrCodeImage src={qrCodeUrl} alt="QR Code para coexistencia" />
                    ) : (
                      <div style={{ textAlign: 'center', padding: '2rem' }}>
                        <p>QR code sendo gerado pela Meta.</p>
                        <p style={{ marginTop: '0.5rem' }}>
                          Acesse o{' '}
                          <a
                            href="https://business.facebook.com/wa/manage/phone-numbers/"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Gerenciador do WhatsApp Business
                          </a>{' '}
                          para obter o QR code de coexistencia.
                        </p>
                      </div>
                    )}
                  </QrCodeContainer>
                  {deepLink && (
                    <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                      <a href={deepLink} target="_blank" rel="noopener noreferrer">
                        Abrir no celular
                      </a>
                    </div>
                  )}
                  <QrCodeInstructions>
                    <QrCodeStep>
                      <QrCodeStepNumber>1</QrCodeStepNumber>
                      <QrCodeStepText>Abra o <strong>WhatsApp</strong> no seu celular</QrCodeStepText>
                    </QrCodeStep>
                    <QrCodeStep>
                      <QrCodeStepNumber>2</QrCodeStepNumber>
                      <QrCodeStepText>
                        Toque em <strong>Dispositivos vinculados</strong> nas configuracoes
                      </QrCodeStepText>
                    </QrCodeStep>
                    <QrCodeStep>
                      <QrCodeStepNumber>3</QrCodeStepNumber>
                      <QrCodeStepText>
                        Toque em <strong>Vincular um dispositivo</strong> e escaneie o QR code
                      </QrCodeStepText>
                    </QrCodeStep>
                  </QrCodeInstructions>
                  <MetaButton onClick={handleQrCodeDone} style={{ marginTop: '1.5rem' }}>
                    Ja escaniei o QR code
                  </MetaButton>
                </>
              )}
            </CardBody>
          </Card>
        )}

        {step === 'success' && (
          <Card>
            <CardHeader>
              <SuccessIcon>&#10003;</SuccessIcon>
              <StepTitle>Coexistencia ativada!</StepTitle>
              <StepSubtitle>
                Conta Meta conectada com sucesso. Use o WhatsApp no celular e via API ao mesmo tempo.
              </StepSubtitle>
            </CardHeader>
            <CardBody>
              <MetaButton onClick={() => history.push('/')}>Ir para conversas</MetaButton>
            </CardBody>
          </Card>
        )}

        {step === ('connected' as Step) && connectionStatus?.connection && (
          <Card>
            <CardHeader>
              <StepTitle>WhatsApp Business conectado</StepTitle>
              <StepSubtitle>
                Coexistencia ativa — usando o WhatsApp no celular e via API simultaneamente.
              </StepSubtitle>
            </CardHeader>
            <CardBody>
              <ConnectedCard>
                <div>
                  <strong>{connectionStatus.connection.verified_name}</strong>
                  <span>{connectionStatus.connection.display_phone_number}</span>
                  {connectionStatus.connection.connected_at && (
                    <small>
                      Conectado em{' '}
                      {new Date(connectionStatus.connection.connected_at).toLocaleDateString('pt-BR', {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </small>
                  )}
                </div>
              </ConnectedCard>
              {error && <ErrorMessage>{error}</ErrorMessage>}
              <DisconnectButton onClick={handleDisconnect} disabled={isLoading}>
                {isLoading ? 'Desconectando...' : 'Desconectar conta Meta'}
              </DisconnectButton>
            </CardBody>
          </Card>
        )}
      </Content>
    </Container>
  );
}

function FbIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
      <path d="M12.001 2.002c-5.522 0-9.999 4.477-9.999 9.999 0 4.99 3.656 9.126 8.437 9.879v-6.988h-2.54v-2.891h2.54V9.798c0-2.508 1.493-3.891 3.776-3.891 1.094 0 2.24.195 2.24.195v2.459h-1.264c-1.24 0-1.628.772-1.628 1.563v1.875h2.771l-.443 2.891h-2.328v6.988C18.344 21.129 22 16.992 22 12.001c0-5.522-4.477-9.999-9.999-9.999z" />
    </svg>
  );
}